import DeleteOutline from "@mui/icons-material/DeleteOutline";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import PersonAddOutlined from "@mui/icons-material/PersonAddOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";

import { grey } from "@mui/material/colors";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import React from "react";
import { useForm, useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import BorrowerExperienceEditor from "../../components/sidebar/LoanBorrowerSidebar/BorrowerExperienceEditor";
import RffBooleanCheckbox from "../../components/finalForm/inputs/RffBooleanCheckbox";
import RffCheckboxArrayField from "../../components/finalForm/inputs/RffCheckboxArrayField";
import RffBooleanToggleField from "../../components/finalForm/inputs/RffBooleanToggleField";
import RffDateField from "../../components/finalForm/inputs/RffDateField";
import RffDollarField from "../../components/finalForm/inputs/RffDollarField";
import RffEmailField from "../../components/finalForm/inputs/RffEmailField";
import RffPhoneField from "../../components/finalForm/inputs/RffPhoneField";
import RffSelectToggleField from "../../components/finalForm/inputs/RffSelectToggleField";
import RffSsnField from "../../components/finalForm/inputs/RffSsnField";
import RffTextareaField from "../../components/finalForm/inputs/RffTextareaField";
import RffTextField from "../../components/finalForm/inputs/RffTextField";
import RffConditional from "../../components/finalForm/shared/RffConditional";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import TitledCard from "../../components/ui/TitledCard/TitledCard";
import RffAddressEditingField from "../../components/finalForm/inputs/RffAddressEditingField";
import RffCheckboxGroup from "../../components/finalForm/shared/RffCheckboxGroup";
import { selectOptionsHousingEvents } from "../../constants/selectOptions/selectOptionsHousingEvents";
import { selectOptionsTimeSinceHousingEvent } from "../../constants/selectOptions/selectOptionsTimeSinceHousingEvent";
import { clearOpenLiensDetails } from "../../constants/borrowers/openLiensDetailFields";

/**
 * "How long ago?" only means something once an event is selected, and it reads
 * as a second unrelated question when nothing is. Keyed off the array rather
 * than a boolean flag so it follows whatever the checklist actually holds.
 */
const HousingEventTiming = ({ fieldName }) => {
  const { values } = useFormState({ subscription: { values: true } });

  if (isEmpty(get(values, `${fieldName}.housing_events`))) {
    return null;
  }

  return (
    <RffSelectToggleField
      name={`${fieldName}.time_since_housing_event`}
      label="Time Since Last Housing Event"
      options={selectOptionsTimeSinceHousingEvent}
    />
  );
};

const BorrowersSection = ({ testing, defaultExpanded, mobile = false }) => {
  const [expanded, setExpanded] = React.useState(0);

  const { change } = useForm();
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const onCitizenshipStatusChange = (fieldName, v) => {
    if (v === "US Citizen") {
      change(`${fieldName}.borrowerHasSSN`, true);
    } else {
      change(`${fieldName}.borrowerHasSSN`, false);
    }
  };

  return (
    <TitledCard
      defaultExpanded={defaultExpanded}
      collapsible
      icon={<PersonOutline className="thin" />}
      variant="h1"
      title="Loan Borrowers"
    >
      <RffGroup
        suppressBottomMargin
        disableGrid
        // titleType="h2"
        // title="Loan Borrowers"
      >
        <FieldArray name={"borrowers"}>
          {({ fields: arrayFields, meta }) => {
            return (
              <React.Fragment>
                {arrayFields.map((fieldName, index) => (
                  <React.Fragment key={fieldName}>
                    <Accordion
                      onChange={handleChange(index)}
                      expanded={expanded === index}
                      sx={{
                        m: "0 !important",
                        borderRadius: "4px",
                        boxShadow: "0 0 0px 1px #dde0e4",
                        overflow: "hidden",
                      }}
                    >
                      <AccordionSummary
                        sx={{
                          background: grey[50],
                          px: 1.5,
                          minHeight: "unset !important",
                          ".MuiAccordionSummary-content": {
                            my: "8px !important",
                          },
                        }}
                        expandIcon={
                          <ExpandMoreRounded sx={{ color: "#232a31" }} />
                        }
                      >
                        <Flx fw jb ac sx={{ color: "#232a31" }}>
                          <Txt
                            bold
                            variant="subtitle2"
                            sx={{ color: "inherit" }}
                          >
                            {`Borrower #${index + 1}`}
                          </Txt>

                          <IconButton
                            sx={{ color: "inherit" }}
                            onClick={() => arrayFields.remove(index)}
                            size="small"
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Flx>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          background: "#ffffff",
                          p: 2.8,
                          borderTop: "1px solid #dde0e4",
                        }}
                      >
                        {/* START BORROWER */}
                        <RffGroup>
                          <RffTextField
                            name={`${fieldName}.firstName`}
                            label="First Name"
                            size={6}
                          />
                          <RffTextField
                            name={`${fieldName}.lastName`}
                            label="Last Name"
                            size={6}
                          />
                          <RffEmailField
                            size={mobile ? 12 : 4}
                            name={`${fieldName}.emailAddress`}
                            label="Email"
                          />
                          <RffPhoneField
                            size={mobile ? 12 : 4}
                            name={`${fieldName}.phone`}
                            label="Phone"
                          />

                          <RffDateField
                            size={mobile ? 12 : 4}
                            name={`${fieldName}.dateOfBirth`}
                            label="Date of birth"
                          />

                          <RffSelectToggleField
                            name={`${fieldName}.citizenshipStatus`}
                            label="Borrower Citizenship"
                            onChange={(v) =>
                              onCitizenshipStatusChange(fieldName, v)
                            }
                            options={[
                              "US Citizen",
                              "Foreign National with SSN",
                              "Foreign National",
                              "Foreign National Non-Resident",
                              "Other - Options do not apply",
                            ]}
                          />

                          <RffConditional
                            operatorForMultiple={"OR"}
                            rules={[
                              {
                                field: `${fieldName}.citizenshipStatus`,
                                operator: "==",
                                value: "US Citizen",
                              },
                              {
                                field: `${fieldName}.citizenshipStatus`,
                                operator: "==",
                                value: "Foreign National with SSN",
                              },
                              {
                                field: `${fieldName}.citizenshipStatus`,
                                operator: "==",
                                value: "Foreign National",
                              },
                            ]}
                          >
                            <RffAddressEditingField
                              name={`${fieldName}.primaryAddress`}
                              label="Primary Address"
                            />
                          </RffConditional>
                          <RffConditional
                            operatorForMultiple={"OR"}
                            rules={[
                              {
                                field: `${fieldName}.citizenshipStatus`,
                                operator: "==",
                                value: "Foreign National Non-Resident",
                              },
                              {
                                field: `${fieldName}.citizenshipStatus`,
                                operator: "==",
                                value: "Other - Options do not apply",
                              },
                              // {
                              //   field: `${fieldName}.citizenshipStatus`,
                              //   operator: "==",
                              //   value: undefined,
                              // },
                            ]}
                          >
                            <RffTextField
                              name={`${fieldName}.nonUSborrowerAddress`}
                              label="Enter non-US address"
                            />
                          </RffConditional>
                          <RffSelectToggleField
                            size={mobile ? 12 : 4}
                            name={`${fieldName}.maritalStatus`}
                            label="Marital Status"
                            options={["Married", "Not Married"]}
                          />
                          <RffBooleanToggleField
                            name={`${fieldName}.borrowerHasSSN`}
                            size={mobile ? 12 : 4}
                            label="Does borrower have a US issued SSN?"
                          />
                          <RffConditional
                            field={`${fieldName}.borrowerHasSSN`}
                            value={true}
                          >
                            <RffSsnField
                              size={mobile ? 12 : 4}
                              name={`${fieldName}.borrowerSSN`}
                              label="Social Security Number"
                            />
                          </RffConditional>
                        </RffGroup>

                        {/* BORROWER BACKGROUND STEP */}
                        <RffGroup
                          groupLabel="Borrower Background"
                          titleType="h3"
                        >
                          <RffDollarField
                            name={`${fieldName}.cashLiquidity`}
                            label="Total cash liquidity in personal and/or entity bank accounts"
                            //
                          />
                          <RffDollarField
                            name={`${fieldName}.otherLiquidity`}
                            label="Total value of other liquidity sources"
                            helperText="Including money market/securities account, cash value of life insurance policies, IRA or 401k accounts"
                          />
                          <RffSelectToggleField
                            name={`${fieldName}.estimatedFICO`}
                            label="Borrower estimated FICO range"
                            options={[
                              "Below 600",
                              "600-620",
                              "620-650",
                              "650-680",
                              "680-700",
                              "700-720",
                              "Greater than 720",
                              "N/A do not have FICO",
                            ]}
                          />
                        </RffGroup>

                        {/* CREDIT & LEGAL HISTORY — the same disclosure
                            checklist, in the same order and wording, that the
                            borrower answers in the loan application, so an
                            operator keying in a paper app reads straight down
                            it. Open liens is the one gating box: ticking it
                            reveals its own follow-up checklist. */}
                        <RffGroup
                          groupLabel="Credit & Legal History"
                          titleType="h3"
                        >
                          <RffCheckboxGroup
                            label="Has the borrower ever:"
                            rowSpacing={1}
                            contentSx={{ px: 0 }}
                          >
                            <RffBooleanCheckbox
                              boxed
                              name={`${fieldName}.application_felony_conviction`}
                              label="Been convicted of a felony"
                            />
                            <RffBooleanCheckbox
                              boxed
                              name={`${fieldName}.application_financial_fraud_misdemeanor`}
                              label="Been convicted of any financial or fraud related crimes, including misdemeanors"
                            />
                            <RffBooleanCheckbox
                              boxed
                              name={`${fieldName}.bankruptcy_flag`}
                              label="Filed for bankruptcy in the last 5 years"
                            />
                            <RffBooleanCheckbox
                              boxed
                              name={`${fieldName}.open_liens_judgments`}
                              label="Had any open charge offs, federal or state tax liens, pending litigation, or financial judgments"
                              onChange={clearOpenLiensDetails(change, fieldName)}
                            />
                          </RffCheckboxGroup>

                          {/* Follow-up reads as a peer of the list above —
                              same label style, two columns like Housing
                              Events — not a nested/indented dependent. */}
                          <RffConditional
                            field={`${fieldName}.open_liens_judgments`}
                            value={true}
                          >
                            <RffCheckboxGroup
                              label="Select all that apply:"
                              rowSpacing={1}
                              contentSx={{ px: 0 }}
                            >
                              <RffBooleanCheckbox
                                boxed
                                size={6}
                                name={`${fieldName}.charge_offs`}
                                label="Charge Offs"
                              />
                              <RffBooleanCheckbox
                                boxed
                                size={6}
                                name={`${fieldName}.tax_liens`}
                                label="Tax Liens"
                              />
                              <RffBooleanCheckbox
                                boxed
                                size={6}
                                name={`${fieldName}.financial_judgements`}
                                label="Financial Judgements"
                              />
                              <RffBooleanCheckbox
                                boxed
                                size={6}
                                name={`${fieldName}.pending_litigation`}
                                label="Pending Litigation"
                              />
                            </RffCheckboxGroup>
                          </RffConditional>
                        </RffGroup>

                        {/* HOUSING EVENTS — its own section, storing the string
                            ARRAY the rest of the app already reads (the
                            borrower sidebar's multi-select, the application
                            PDF, the drilldown search index). It used to be a
                            boolean here with three sibling flags, one of which
                            (`mortgage_lates`) is a multi-select of late buckets
                            on the deal — so a checked box wrote a bool into a
                            list field and nothing downstream could read it
                            back. A checklist rather than a dropdown: these
                            options ARE the answer to the question above them. */}
                        <RffGroup groupLabel="Housing Events" titleType="h3">
                          <RffCheckboxArrayField
                            name={`${fieldName}.housing_events`}
                            label="In the last three years, has the borrower entered into a loan forbearance or modification program, had a property owned individually or through an entity under their control subject to lis pendens or foreclosure, or had any mortgage lates?"
                            options={selectOptionsHousingEvents}
                            helperText="Select all that apply — leave blank if none of these apply"
                            columns={2}
                          />
                          <HousingEventTiming fieldName={fieldName} />
                        </RffGroup>

                        {/* EXPERIENCE — categories first, counts only for the
                            ones that apply. Shares BorrowerExperienceEditor
                            with the borrower sidebar so intake and later edits
                            write the same per-category buckets; it replaces the
                            old always-six-rows grid, which asked a fix-and-flip
                            borrower for ground-up numbers. */}
                        <RffGroup groupLabel="Experience" titleType="h3">
                          <RffCheckboxGroup
                            label="Primary residence"
                            rowSpacing={1}
                            contentSx={{ px: 0 }}
                          >
                            <RffBooleanCheckbox
                              boxed
                              name={`${fieldName}.firstTimeHomebuyer`}
                              label="Has previously owned a primary residence"
                            />
                          </RffCheckboxGroup>

                          <RffGroup
                            disableGrid
                            isGridChild
                            groupLabel="Which of these has the borrower done before?"
                          >
                            <Txt secondary sx={{ fontSize: "13px" }}>
                              Select all that apply, then fill in the counts
                              that appear — or describe the experience below.
                            </Txt>
                            <BorrowerExperienceEditor prefix={fieldName} />
                          </RffGroup>

                          <RffTextareaField
                            name={`${fieldName}.borrowerExperienceSummary`}
                            label="Summary of borrower property investor experience"
                            minHeight="100px"
                            placeholder="Describe the projects the borrower has taken on — scope, timelines, and outcomes are all useful."
                          />
                        </RffGroup>
                      </AccordionDetails>
                    </Accordion>
                  </React.Fragment>
                ))}

                <Flx
                  center
                  fw
                  item
                  sx={{
                    color: "#232a31",
                  }}
                >
                  <Button
                    variant="text"
                    // size="medium"
                    // color="inherit"
                    fullWidth
                    startIcon={<PersonAddOutlined />}
                    onClick={() => {
                      arrayFields.push({});
                    }}
                  >
                    Add Borrower
                  </Button>
                  {meta.touched && typeof meta.error === "string" && (
                    <div style={{ color: "red", marginTop: "4px" }}>
                      {meta.error}
                    </div>
                  )}
                </Flx>
              </React.Fragment>
            );
          }}
        </FieldArray>
        {/* <Divider /> */}
      </RffGroup>
    </TitledCard>
  );
};

export default BorrowersSection;
